/**
 * Aurora Ribbons Visualizer - Flowing ribbon curtains resembling aurora borealis
 *
 * Ribbons sweep horizontally with smooth sinusoidal motion, react to audio bands
 * (bass = amplitude, mids = wave complexity, treble = shimmer sparkles), and pulse
 * upward on beat detection. Uses offscreen canvas trail technique for faint ghosting
 * that respects the global background controller.
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Vignette — radial gradient from transparent center to dark edges
 * 3. Ribbons — filled shapes with quadratic Bezier curves + additive highlight strokes
 * 4. Shimmer — sparkle particles near ribbon edges (additive blending)
 * 5. Beat flash — brief white flash on beat detection
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'aurora-ribbons',
  displayName: 'Aurora Ribbons',
  contextType: '2d',
};

// Pre-allocated pool sizes
const MAX_SHIMMER = 200;
const MAX_PULSES = 16;

class AuroraRibbons extends Base2DVisualizer {
  static name = 'aurora-ribbons';
  static displayName = 'Aurora Ribbons';

  static getConfigUI() {
    return [
      { type: 'range', id: 'ribbonCount', label: 'Ribbon Count', min: 2, max: 8, default: 4 },
      { type: 'range', id: 'pointCount', label: 'Control Points', min: 12, max: 48, default: 24 },
      { type: 'range', id: 'speed', label: 'Flow Speed', min: 1, max: 10, default: 5, scale: 0.1 },
      { type: 'range', id: 'waveStrength', label: 'Wave Strength', min: 1, max: 20, default: 10, scale: 0.01 },
      { type: 'range', id: 'pulseStrength', label: 'Pulse Strength', min: 1, max: 20, default: 12, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 5, max: 40, default: 12, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 30, default: 15 },
      { type: 'range', id: 'shimmerAmount', label: 'Shimmer Amount', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'vignetteStrength', label: 'Vignette', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 50, scale: 0.01 },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#00ffaa' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#ff00ff' },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return 96;
  }

  init() {
    this.time = 0;
    this.isFirstFrame = true;

    // Trail offscreen canvas — stores previous frame for fade compositing
    this._trailCanvas = null;
    this._trailCtx = null;

    // Vignette gradient cache
    this._vignetteGradient = null;
    this._vignetteW = 0;
    this._vignetteH = 0;

    // Color cache (parsed RGB from hex)
    this._csR = 0; this._csG = 0; this._csB = 0;
    this._ceR = 0; this._ceG = 0; this._ceB = 0;
    this._cachedCS = '';
    this._cachedCE = '';
    this._ensureColorCache(true);

    // Ribbon state
    this._initRibbons();

    // Shimmer particle pool (fixed-size, no per-frame allocation)
    this._initShimmerPool();

    // Beat pulse pool (fixed-size, circular allocation)
    this.pulses = new Array(MAX_PULSES);
    for (let i = 0; i < MAX_PULSES; i++) {
      this.pulses[i] = { position: 0, strength: 0, ribbonIndex: 0, active: false };
    }
    this._nextPulse = 0;

    // Accumulated energy-driven phase boost for audio-modulated flow speed
    this._energyPhaseBoost = 0;

    // Reusable midpoint coordinates to avoid per-iteration object allocation
    this._midX = 0;
    this._midY = 0;
  }

  onConfigChange(changed) {
    if (changed.ribbonCount !== undefined || changed.pointCount !== undefined) {
      this._initRibbons();
    }
    if (changed.vignetteStrength !== undefined) {
      this._vignetteGradient = null;
    }
    if (changed.colorStart !== undefined || changed.colorEnd !== undefined) {
      this._ensureColorCache(true);
    }
  }

  /**
   * Parse colorStart/colorEnd hex values into RGB components for fast per-frame use.
   */
  _ensureColorCache(force = false) {
    const cs = this.config.colorStart || '#00ffaa';
    const ce = this.config.colorEnd || '#ff00ff';

    if (force || this._cachedCS !== cs) {
      this._csR = parseInt(cs.slice(1, 3), 16) || 0;
      this._csG = parseInt(cs.slice(3, 5), 16) || 0;
      this._csB = parseInt(cs.slice(5, 7), 16) || 0;
      this._cachedCS = cs;
    }
    if (force || this._cachedCE !== ce) {
      this._ceR = parseInt(ce.slice(1, 3), 16) || 0;
      this._ceG = parseInt(ce.slice(3, 5), 16) || 0;
      this._ceB = parseInt(ce.slice(5, 7), 16) || 0;
      this._cachedCE = ce;
    }
  }

  /**
   * Initialize ribbon array with Float64Array control points.
   * Ribbons are distributed vertically across 25%–75% of the canvas.
   */
  _initRibbons() {
    const count = this.config.ribbonCount;
    const pointCount = this.config.pointCount;

    this.ribbons = new Array(count);
    for (let i = 0; i < count; i++) {
      const t = count > 1 ? i / (count - 1) : 0.5;
      this.ribbons[i] = {
        points: new Float64Array(pointCount * 2), // x, y interleaved
        phase: Math.random() * Math.PI * 2,
        speed: 0.8 + Math.random() * 0.4,
        baseY: 0.25 + t * 0.5,
        hueShift: t,
        thickness: 30 + Math.random() * 20,
        waveFreq: 1.5 + Math.random() * 1.0,
        energy: 0, // per-ribbon band energy, updated each frame
      };
    }
  }

  /**
   * Initialize shimmer particle pool with fixed-size slots.
   */
  _initShimmerPool() {
    this.shimmerPool = new Array(MAX_SHIMMER);
    for (let i = 0; i < MAX_SHIMMER; i++) {
      this.shimmerPool[i] = { x: 0, y: 0, life: 0, maxLife: 0, size: 0, alpha: 0, active: false };
    }
    this._nextShimmer = 0;
  }

  /**
   * Ensure the trail offscreen canvas matches the main canvas dimensions.
   */
  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Instead of overlaying a semi-transparent solid color (which ignores the global
   * background and always converges to that color), we:
   * 1. Save the current canvas (previous frame) to an offscreen buffer
   * 2. Draw the proper background via clearCanvas() -> drawBackground()
   * 3. Composite the saved frame on top at reduced opacity (trail effect)
   * 4. Render new content
   *
   * This works correctly with solid colors, gradients, patterns, and images.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters (bassEnergy, beatDetected, etc.)
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      // Trail effect via offscreen canvas compositing
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      //    trailAlpha controls fade speed: higher = faster fade (less persistence)
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  /**
   * Main render: vignette -> ribbons -> shimmer -> beat flash.
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const dt = 1 / 60; // approximate frame delta
    this.time += dt;

    // Compute band energies from the 96-bin amplitude array (split into 3 bands)
    const third = Math.floor(amplitudes.length / 3);
    let bassSum = 0, midSum = 0, highSum = 0;
    for (let i = 0; i < third; i++) bassSum += amplitudes[i];
    for (let i = third; i < third * 2; i++) midSum += amplitudes[i];
    for (let i = third * 2; i < amplitudes.length; i++) highSum += amplitudes[i];
    const bassEnergy = bassSum / third;
    const midEnergy = midSum / third;
    const highEnergy = highSum / (amplitudes.length - third * 2);
    const overallEnergy = (bassEnergy + midEnergy + highEnergy) / 3;

    // Accumulate energy-driven phase boost — ribbons flow faster with music
    this._energyPhaseBoost += overallEnergy * dt * 3;

    // Beat detection -> spawn traveling pulses on ribbons
    if (this.beatDetected) {
      const targetRibbon = Math.floor(Math.random() * this.ribbons.length);
      this._spawnPulse(targetRibbon);
      // Spawn on adjacent ribbon for visual spread
      if (this.ribbons.length > 1) {
        const adj = (targetRibbon + (Math.random() > 0.5 ? 1 : -1) + this.ribbons.length) % this.ribbons.length;
        this._spawnPulse(adj);
      }
    }

    // Update pulse positions and decay
    this._updatePulses(dt);

    // --- Layer 1: Vignette ---
    if (config.vignetteStrength > 0) {
      this._drawVignette();
    }

    // --- Layer 2: Ribbons (back to front) ---
    for (let i = 0; i < this.ribbons.length; i++) {
      const ribbon = this.ribbons[i];
      this._updateRibbonPoints(ribbon, amplitudes, dt, i, bassEnergy, midEnergy, overallEnergy);
      this._drawRibbon(ribbon, i);
    }

    // --- Layer 3: Shimmer sparkles ---
    this._drawShimmer(dt, highEnergy);

    // --- Layer 4: Beat flash ---
    if (this.beatDetected) {
      ctx.save();
      ctx.globalAlpha = 0.12 + this.beatConfidence * 0.15;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }
  }

  /**
   * Update ribbon control point positions based on time, audio, and pulses.
   *
   * Each ribbon focuses on a specific frequency sub-range: ribbon 0 (bottom) responds
   * to bass, ribbon N (top) responds to treble. This creates visual frequency separation
   * where you can "see" different bands moving through different ribbon layers.
   *
   * Point Y = baseY + (primaryWave + harmonics + localAmp + pulseOffset) * bassScale
   */
  _updateRibbonPoints(ribbon, amplitudes, dt, ribbonIndex, bassEnergy, midEnergy, overallEnergy) {
    const { canvas, config } = this;
    const pointCount = config.pointCount;
    const w = canvas.width;
    const h = canvas.height;
    const ribbonCount = this.ribbons.length;

    const baseY = ribbon.baseY * h;

    // Flow speed modulated by overall energy — ribbons accelerate with music
    const phase = ribbon.phase + (this.time * config.speed + this._energyPhaseBoost) * ribbon.speed;

    // Per-ribbon frequency band mapping: each ribbon focuses on a spectral sub-range
    const bandWidth = amplitudes.length / ribbonCount;
    const bandStart = Math.floor(ribbonIndex * bandWidth);
    const bandEnd = Math.floor((ribbonIndex + 1) * bandWidth);

    // Compute this ribbon's specific band energy
    let ribbonBandSum = 0;
    for (let b = bandStart; b < bandEnd; b++) ribbonBandSum += amplitudes[b];
    const ribbonEnergy = ribbonBandSum / (bandEnd - bandStart);

    // Store on ribbon object for use by _drawRibbon (thickness, opacity)
    ribbon.energy = ribbonEnergy;

    for (let p = 0; p < pointCount; p++) {
      const t = p / (pointCount - 1);

      // X position: evenly distributed across canvas width
      const x = t * w;

      // Primary sinusoidal wave — amplitude scales with ribbon's own band energy
      const primaryWave = Math.sin(phase + t * ribbon.waveFreq * Math.PI * 2)
        * config.waveStrength * h * 0.3 * (0.3 + ribbonEnergy * 1.5);

      // Harmonic waves modulated by mid energy (more mids = more complex wave shape)
      const harmonic1 = Math.sin(phase * 1.7 + t * 4.3)
        * config.waveStrength * h * 0.12 * midEnergy;
      const harmonic2 = Math.sin(phase * 2.3 + t * 7.1)
        * config.waveStrength * h * 0.06 * midEnergy;

      // Local amplitude from this ribbon's frequency sub-range
      const ampIndex = bandStart + Math.floor(t * (bandEnd - bandStart - 1));
      const localAmp = amplitudes[ampIndex] || 0;
      const ampOffset = localAmp * h * 0.12 * (1 + bassEnergy);

      // Pulse offset — traveling waves along the ribbon on beats
      let pulseOffset = 0;
      for (let j = 0; j < MAX_PULSES; j++) {
        const pulse = this.pulses[j];
        if (!pulse.active || pulse.ribbonIndex !== ribbonIndex) continue;

        const dist = Math.abs(t - pulse.position);
        if (dist < 0.3) {
          const falloff = 1 - dist / 0.3;
          pulseOffset -= falloff * falloff * pulse.strength * config.pulseStrength * h * 0.3;
        }
      }

      // Combine — bass widens the range dramatically (silence=20%, max bass=100%)
      const y = baseY + (primaryWave + harmonic1 + harmonic2 + ampOffset + pulseOffset)
        * (0.2 + bassEnergy * 0.8);

      // Store in Float64Array (interleaved x, y)
      ribbon.points[p * 2] = x;
      ribbon.points[p * 2 + 1] = y;
    }
  }

  /**
   * Draw a single ribbon using quadratic Bezier midpoint curves.
   * Pass 1: source-over fill with linear gradient (the ribbon body)
   * Pass 2: lighter composite thin highlight stroke with shadowBlur glow
   */
  _drawRibbon(ribbon, index) {
    const { ctx, canvas, config } = this;
    const pointCount = config.pointCount;
    const points = ribbon.points;
    const ribbonEnergy = ribbon.energy || 0;
    const thickness = ribbon.thickness * (0.4 + ribbonEnergy * 1.6);
    const halfT = thickness / 2;

    // Interpolate color for this ribbon based on its hueShift (0-1)
    const t = ribbon.hueShift;
    const r = Math.round(this._csR + (this._ceR - this._csR) * t);
    const g = Math.round(this._csG + (this._ceG - this._csG) * t);
    const b = Math.round(this._csB + (this._ceB - this._csB) * t);

    // Linear gradient along canvas width for edge fade
    // Opacity driven by this ribbon's own band energy for frequency-specific breathing
    const grad = ctx.createLinearGradient(0, 0, canvas.width, 0);
    const alpha = 0.2 + ribbonEnergy * 0.7;
    grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha * 0.3})`);
    grad.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, ${alpha})`);
    grad.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${alpha})`);
    grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${alpha * 0.3})`);

    // --- Pass 1: Fill ribbon shape (source-over) ---
    ctx.save();
    ctx.fillStyle = grad;
    ctx.beginPath();

    // Top edge: control points shifted up by half thickness (left to right)
    ctx.moveTo(points[0], points[1] - halfT);

    for (let i = 1; i < pointCount - 1; i++) {
      const cx = points[i * 2];
      const cy = points[i * 2 + 1] - halfT;
      const nx = points[(i + 1) * 2];
      const ny = points[(i + 1) * 2 + 1] - halfT;
      this._midX = (cx + nx) / 2;
      this._midY = (cy + ny) / 2;
      ctx.quadraticCurveTo(cx, cy, this._midX, this._midY);
    }
    // Finish top edge at last point
    ctx.lineTo(points[(pointCount - 1) * 2], points[(pointCount - 1) * 2 + 1] - halfT);

    // Right end cap: straight line down to bottom edge
    ctx.lineTo(points[(pointCount - 1) * 2], points[(pointCount - 1) * 2 + 1] + halfT);

    // Bottom edge: control points shifted down (right to left)
    for (let i = pointCount - 2; i > 0; i--) {
      const cx = points[i * 2];
      const cy = points[i * 2 + 1] + halfT;
      const prevX = points[(i - 1) * 2];
      const prevY = points[(i - 1) * 2 + 1] + halfT;
      this._midX = (cx + prevX) / 2;
      this._midY = (cy + prevY) / 2;
      ctx.quadraticCurveTo(cx, cy, this._midX, this._midY);
    }
    // Finish bottom edge at first point
    ctx.lineTo(points[0], points[1] + halfT);

    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // --- Pass 2: Highlight center stroke (additive blending for glow) ---
    if (config.glowIntensity > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.2 + ribbonEnergy * 0.6;
      ctx.strokeStyle = `rgba(${Math.min(255, r + 60)}, ${Math.min(255, g + 60)}, ${Math.min(255, b + 60)}, 0.8)`;
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = config.glowIntensity;
      ctx.shadowColor = `rgb(${r}, ${g}, ${b})`;

      ctx.beginPath();
      ctx.moveTo(points[0], points[1]);
      for (let i = 1; i < pointCount - 1; i++) {
        const cx = points[i * 2];
        const cy = points[i * 2 + 1];
        const nx = points[(i + 1) * 2];
        const ny = points[(i + 1) * 2 + 1];
        this._midX = (cx + nx) / 2;
        this._midY = (cy + ny) / 2;
        ctx.quadraticCurveTo(cx, cy, this._midX, this._midY);
      }
      ctx.lineTo(points[(pointCount - 1) * 2], points[(pointCount - 1) * 2 + 1]);
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.restore();
    }
  }

  /**
   * Draw radial vignette from transparent center to dark edges.
   * Gradient is cached and only rebuilt on canvas resize.
   */
  _drawVignette() {
    const { ctx, canvas, config } = this;

    if (this._vignetteW !== canvas.width || this._vignetteH !== canvas.height || !this._vignetteGradient) {
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const radius = Math.max(canvas.width, canvas.height) * 0.7;
      this._vignetteGradient = ctx.createRadialGradient(cx, cy, radius * 0.3, cx, cy, radius);
      this._vignetteGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      this._vignetteGradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
      this._vignetteW = canvas.width;
      this._vignetteH = canvas.height;
    }

    ctx.save();
    ctx.globalAlpha = config.vignetteStrength;
    ctx.fillStyle = this._vignetteGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  /**
   * Spawn, update, and draw shimmer particles near ribbon edges.
   * Uses a circular pool allocation to avoid GC pressure.
   */
  _drawShimmer(dt, highEnergy) {
    const { ctx, canvas, config } = this;

    // Spawn new shimmer particles based on treble energy
    const spawnCount = Math.floor(highEnergy * config.shimmerAmount * 5);
    for (let s = 0; s < spawnCount; s++) {
      const particle = this.shimmerPool[this._nextShimmer];
      this._nextShimmer = (this._nextShimmer + 1) % MAX_SHIMMER;

      // Place near a random ribbon's control point
      const ribbonIdx = Math.floor(Math.random() * this.ribbons.length);
      const ribbon = this.ribbons[ribbonIdx];
      const pointIdx = Math.floor(Math.random() * config.pointCount);
      const px = ribbon.points[pointIdx * 2];
      const py = ribbon.points[pointIdx * 2 + 1];

      particle.x = px + (Math.random() - 0.5) * ribbon.thickness * 2;
      particle.y = py + (Math.random() - 0.5) * ribbon.thickness;
      particle.life = 1;
      particle.maxLife = 0.5 + Math.random() * 0.5;
      particle.size = 1 + Math.random() * 2;
      particle.alpha = 0.5 + Math.random() * 0.5;
      particle.active = true;
    }

    // Update and draw all active shimmer particles
    // Use source-over (not lighter) to avoid additive blowout through trail accumulation
    ctx.save();
    ctx.fillStyle = '#ffffff';

    for (let i = 0; i < MAX_SHIMMER; i++) {
      const p = this.shimmerPool[i];
      if (!p.active) continue;

      p.life -= dt / p.maxLife;
      if (p.life <= 0) {
        p.active = false;
        continue;
      }

      // Slight upward drift
      p.y -= 15 * dt;

      // Quadratic falloff for natural fade
      const lifeAlpha = p.life * p.life;
      ctx.globalAlpha = p.alpha * lifeAlpha * 0.6;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * lifeAlpha, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Spawn a beat pulse on a specific ribbon from the circular pool.
   */
  _spawnPulse(ribbonIndex) {
    const pulse = this.pulses[this._nextPulse];
    this._nextPulse = (this._nextPulse + 1) % MAX_PULSES;

    pulse.position = Math.random(); // random start position along ribbon (0-1)
    pulse.strength = 0.7 + this.beatConfidence * 0.5;
    pulse.ribbonIndex = ribbonIndex;
    pulse.active = true;
  }

  /**
   * Update pulse positions (traveling wave) and decay their strength.
   */
  _updatePulses(dt) {
    for (let i = 0; i < MAX_PULSES; i++) {
      const pulse = this.pulses[i];
      if (!pulse.active) continue;

      // Traveling wave: move pulse along ribbon
      pulse.position += dt * 1.5;

      // Exponential decay
      pulse.strength *= 0.92;

      if (pulse.strength < 0.01 || pulse.position > 1.3) {
        pulse.active = false;
      }
    }
  }

  /**
   * Clear canvas and reset all runtime state.
   */
  clear() {
    super.clear();
    this.time = 0;
    this._energyPhaseBoost = 0;
    this.isFirstFrame = true;

    // Reset pulses
    for (let i = 0; i < MAX_PULSES; i++) {
      this.pulses[i].active = false;
    }
    this._nextPulse = 0;

    // Reset shimmer
    for (let i = 0; i < MAX_SHIMMER; i++) {
      this.shimmerPool[i].active = false;
    }
    this._nextShimmer = 0;

    // Reinitialize ribbon control points
    this._initRibbons();

    // Clear trail buffer so stale frames don't flash on restart
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }

    // Invalidate vignette cache
    this._vignetteGradient = null;
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(AuroraRibbons);

export { AuroraRibbons };
export default AuroraRibbons;
